import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { DEFAULT_UNIT_ID, DefaultMapping, Unit } from '@synergy/models';
import { Dispatch } from 'redux';
import { PaginatedResponse } from '@synergy/api';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';
import partition from 'lodash.partition';

import { Api } from '../../api';
import { AppState } from '../../store/models/app-state';

export const CANCEL_CREATE_UNIT = 'CANCEL_CREATE_UNIT';
export const CANCEL_DELETE_UNIT = 'CANCEL_DELETE_UNIT';
export const CANCEL_PATCH_UNIT = 'CANCEL_PATCH_UNIT';
export const CANCEL_UPDATE_UNIT_MAPPING = 'CANCEL_UPDATE_UNIT_MAPPING';
export const CREATE_UNIT_COMPLETED = 'CREATE_UNIT_COMPLETED';
export const DELETE_UNIT_COMPLETED = 'DELETE_UNIT_COMPLETED';
export const PATCH_UNIT_COMPLETED = 'PATCH_UNIT_COMPLETED';
export const RECEIVE_ALL_UNITS = 'RECEIVE_ALL_UNITS';
export const RECEIVE_UNIT_DEFAULT_MAPPING = 'RECEIVE_UNIT_DEFAULT_MAPPING';
export const RECEIVE_UNIT_DEFAULT_MAPPINGS = 'RECEIVE_UNIT_DEFAULT_MAPPINGS';
export const RECEIVE_UNITS = 'RECEIVE_UNITS';
export const REQUEST_ALL_UNITS = 'REQUEST_ALL_UNITS';
export const REQUEST_UNITS = 'REQUEST_UNITS';
export const START_CREATE_UNIT = 'START_CREATE_UNIT';
export const START_DELETE_UNIT = 'START_DELETE_UNIT';
export const START_PATCH_UNIT = 'START_PATCH_UNIT';
export const START_UPDATE_UNIT_MAPPING = 'START_UPDATE_UNIT_MAPPING';

const startCreate = () => {
    return {
        type: START_CREATE_UNIT,
    };
};

const startPatch = () => {
    return {
        type: START_PATCH_UNIT,
    };
};

const requestAllUnits = () => {
    return {
        type: REQUEST_ALL_UNITS,
    };
};

const requestUnits = () => {
    return {
        type: REQUEST_UNITS,
    };
};

const receiveAllUnits = (units: Unit[]) => {
    return {
        type: RECEIVE_ALL_UNITS,
        units,
    };
};

const receiveUnitMapping = (defaultMapping: DefaultMapping<Unit>) => {
    return {
        type: RECEIVE_UNIT_DEFAULT_MAPPING,
        defaultMapping,
    };
};

const receiveUnitMappings = (defaultMappings: PaginatedResponse<DefaultMapping<Unit>>) => {
    return {
        type: RECEIVE_UNIT_DEFAULT_MAPPINGS,
        defaultMappings,
    };
};

const startUpdateMapping = () => {
    return {
        type: START_UPDATE_UNIT_MAPPING,
    };
};

const cancelUpdateMapping = () => {
    return {
        type: CANCEL_UPDATE_UNIT_MAPPING,
    };
};

const receiveUnits = (paginatedUnits: PaginatedResponse<Unit>) => {
    return {
        type: RECEIVE_UNITS,
        paginatedUnits,
    };
};

const startDelete = () => {
    return {
        type: START_DELETE_UNIT,
    };
};

const cancelDelete = () => {
    return {
        type: CANCEL_DELETE_UNIT,
    };
};

const deleteCompleted = (unitId: number) => {
    return {
        type: DELETE_UNIT_COMPLETED,
        unitId,
    };
};

const patchCompleted = (unit: Unit) => {
    return {
        type: PATCH_UNIT_COMPLETED,
        unit,
    };
};

const cancelPatch = () => {
    return {
        type: CANCEL_PATCH_UNIT,
    };
};

const createCompleted = (unit: Unit) => {
    return {
        type: CREATE_UNIT_COMPLETED,
        unit,
    };
};

const cancelCreate = () => {
    return {
        type: CANCEL_CREATE_UNIT,
    };
};

export const createUnit = (unit: Partial<Unit>) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(startCreate());
        const response = await api.unit.createUnit(unit);

        if (response.success === true) {
            dispatch(createCompleted(response.result));
            dispatch(Snackbar.enqueue(PoolNotification.success('app.category.created')));
        } else {
            dispatch(cancelCreate());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const patchUnit = (unit: Partial<Unit>) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(startPatch());
        const response = await api.unit.update(unit);

        if (response.success === true) {
            dispatch(patchCompleted(response.result));
        } else {
            dispatch(cancelPatch());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const deleteUnit = (unitId: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        if (unitId === DEFAULT_UNIT_ID) {
            return;
        }
        dispatch(startDelete());
        const response = await api.unit.delete(unitId);

        if (response.success === true) {
            dispatch(deleteCompleted(unitId));
        } else {
            dispatch(cancelDelete());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const fetchUnits = (searchParams: URLSearchParams) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestUnits());

        const response = await api.unit.cget(searchParams);

        if (response.success) {
            dispatch(receiveUnits(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const fetchAllUnits = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestAllUnits());

        const response = await api.unit.cget(new URLSearchParams({ limit: '10000' }));
        const locale = getState().currentLocale;

        if (response.success) {
            const [primary, secondary] = partition(
                response.result.items,
                unit => unit.order !== undefined && unit.order !== null
            );

            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const primaryUnits = primary.sort((unitA, unitB) => unitA.order! - unitB.order!);
            const secondaryUnits = secondary.sort((unitA, unitB) => {
                const stringA = unitA.translations[locale]?.toUpperCase() ?? '';
                const stringB = unitB.translations[locale]?.toUpperCase() ?? '';
                return stringA < stringB ? -1 : stringA > stringB ? 1 : 0;
            });
            const sortedUnits = [...primaryUnits, ...secondaryUnits];
            dispatch(receiveAllUnits(sortedUnits));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const fetchUnitsMapping = (searchParams: URLSearchParams) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestUnits());

        const response = await api.unit.getDefaultMapping(searchParams);

        if (response.success) {
            dispatch(receiveUnitMappings(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const updateDefaultMapping = (mapping: DefaultMapping) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(startUpdateMapping());
        const response = await api.unit.updateDefaultMapping(mapping);

        if (response.success === true) {
            dispatch(receiveUnitMapping(response.result));
        } else {
            dispatch(cancelUpdateMapping());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};
